/**
 * Where a seller's public profile lives.
 *
 * Company sellers get the shop name in the address, the way listing pages carry
 * the listing title: /u/dackspecialisten-ab/691f2cda-…. The slug is cosmetic —
 * everything keys on the UUID — so a stale slug still resolves, and ProfilePage
 * then redirects it to the canonical one.
 *
 * Private sellers keep the plain /u/:id. Their pages are noindex for GDPR
 * reasons, so putting a person's name in the URL would only spread it further.
 *
 * Keep in step with the server-side twin used by the profile sitemap
 * (server/api-util/seoSitemap/seoSitemapUrls.js). If they disagree, the sitemap
 * starts recommending URLs that immediately redirect.
 */
import { createSlug } from './urlHelpers';

// createSlug falls back to this when a name has no usable characters at all.
const EMPTY_SLUG = 'no-slug';

/**
 * @param {Object} user a user entity
 * @return {Boolean} true for a company ("Återförsäljare") account
 */
export const isBusinessProfile = user =>
  user?.attributes?.profile?.publicData?.userType === 'business';

/**
 * @param {Object} user a user entity
 * @return {String|null} the URL slug for the shop name, or null when there is
 *   nothing usable to put in the address
 */
export const profileSlug = user => {
  const displayName = user?.attributes?.profile?.displayName;
  const slug = displayName ? createSlug(displayName) : null;
  return slug && slug !== EMPTY_SLUG ? slug : null;
};

/**
 * Route name and params for a user's public profile.
 *
 * @param {Object} user a user entity
 * @return {Object|null} { name, params } for NamedLink / createResourceLocatorString,
 *   or null when the user has no id yet (still loading)
 */
export const profilePageLinkProps = user => {
  const id = user?.id?.uuid;
  if (!id) {
    return null;
  }

  const slug = isBusinessProfile(user) ? profileSlug(user) : null;
  return slug
    ? { name: 'ProfilePageSlugged', params: { id, slug } }
    : { name: 'ProfilePage', params: { id } };
};

/**
 * Where this profile should be sent, if it is not already at its canonical
 * address. A store reached through /u/:id or through a stale slug — after a
 * rename, say — belongs at /u/:slug/:id; a private seller reached through a
 * slugged URL belongs back at /u/:id.
 *
 * @param {Object} user a user entity
 * @param {Object} pathParams params of the route being rendered
 * @return {Object|null} { name, params } to redirect to, or null to stay put
 */
export const profileRedirectProps = (user, pathParams) => {
  // No :id in the path means we are not rendering a recognisable profile route,
  // so there is no address to correct and nothing to compare against.
  if (!pathParams?.id) {
    return null;
  }

  const canonical = profilePageLinkProps(user);
  if (!canonical) {
    // Still loading. The slug comes from the display name, so until the user
    // arrives we cannot tell whether this address is the right one.
    return null;
  }

  const isOnCanonicalUrl =
    canonical.name === 'ProfilePageSlugged'
      ? pathParams.slug === canonical.params.slug
      : !pathParams.slug;

  return isOnCanonicalUrl ? null : canonical;
};
